import { Controller, FormProvider, useForm } from 'react-hook-form';
import FieldRow from 'components/FormElements/FieldRow';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import { CalendarDay } from 'further-ui/utils';
import GridContainer from 'components/GridContainer';
import { Grid } from '@mui/material';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import {
  CompanyBreakdownDisplayMode,
  NoSubscriptionsInvestorLandingPage,
  TaxReliefDisplayMode,
  InvestmentIdentifierDisplayMode,
  ShowSubscriptionPagePerformanceChartMode,
  SyndicatesArrangement,
} from 'further-types/firm';
import { TileType } from 'further-types/firm';
import { useFlag } from '@reflag/react-sdk';
import TextField from 'components/FormElements/AppTextInput';
import { forwardRef } from 'react';
import { SelectProps } from '@mui/material/Select';
import Spacing from 'components/Spacing';
import FieldSwitch from 'components/FormElements/FieldSwitch';
import api from 'lib/trpcClient';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';
import { eisFirm as rules } from 'further-ui/validations';
import PageSection from 'components/PageSection';
import { zodResolver } from '@hookform/resolvers/zod';

import ButtonGroup from 'components/ButtonGroup';
import { FIRM } from 'adminConstants/routes';
import Button from 'components/Button';
import { useNotification } from 'hooks/ui/useNotification';
import { addHttps } from 'utils/strings';
import CkEditor from 'components/CkEditor';
import useStyles from './commonStyles';
import ClassificationCheckbox from './ClassificationCheckbox';
import { Row } from 'components/Layout';
import { styled } from '@mui/material';
import { RolePermission } from 'further-types/admin-user-role';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import Dropzone from 'components/Dropzone';
import { UploadType } from 'further-types/files';
import { FileType } from 'further-ui/utils';
import useFileUpload from 'hooks/ui/useFileUpload';

type FormValues = z.infer<typeof rules.UpdateEisPortalAndKycSettingsInput>;

type ConfigurableTilesDropDownProps = {
  onChange: (value: string) => void;
  error?: boolean;
  errorMessage?: string;
  name: string;
  value?: TileType;
} & Omit<SelectProps, 'onChange' | 'size'>;

const CheckBoxRow = styled(Row)({
  flexWrap: 'wrap',
});

const ConfigurableTilesDropDown = forwardRef<
  HTMLDivElement,
  ConfigurableTilesDropDownProps
>(({ onChange, name, error, errorMessage, ...rest }, ref) => (
  <GridContainer>
    <Grid
      size={{
        xs: 12,
        sm: 6,
      }}
    >
      <AppSelectBox
        ref={ref}
        data={[
          {
            value: TileType.MULTIPLE,
            label: 'Multiple on investment',
          },
          {
            value: TileType.MOIC,
            label: 'Multiple on invested capital',
          },
          {
            value: TileType.SUBSCRIPTIONS,
            label: 'Total subscriptions',
          },
          {
            value: TileType.DISTRIBUTABLE,
            label: 'Distributable balance',
          },
          {
            value: TileType.HELD_BY_MANAGER_AWAITING_INVESTMENT,
            label: 'Paid-up capital awaiting deployment',
          },
        ]}
        valueKey="value"
        labelKey="label"
        onChange={(e) => onChange(e.target.value as string)}
        error={error}
        helperText={errorMessage}
        {...rest}
      />
    </Grid>
  </GridContainer>
));

const InvestorPortalSettings: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const apiUtils = api.useUtils();
  const { success, error } = useNotification();
  const { classes } = useStyles();
  const [canEditFirm] = useHasPermissions([RolePermission.EditFirm]);
  const { uploadFile } = useFileUpload();

  const { data: eisFirm } = api.eisFirm.getById.useQuery(
    { firmId: id ?? '' },
    { enabled: !!id },
  );

  const { mutate: updateEisFirm, isPending: isUpdating } =
    api.eisFirm.updatePortalAndKycSettings.useMutation({
      onSuccess: () => {
        success('Firm updated successfully.');
        apiUtils.eisFirm.getById.invalidate();
      },
      onError: (err) => {
        error(err?.message ?? 'Something went wrong.');
      },
    });

  const formMethods = useForm<FormValues>({
    shouldFocusError: true,
    resolver: zodResolver(rules.UpdateEisPortalAndKycSettingsInput),
    values: {
      firmId: id ?? '',
      kycKybReviewRecipient: eisFirm?.kycKybReviewRecipient ?? '',
      amlReviewRecipient: eisFirm?.amlReviewRecipient ?? '',
      whiteLabelConfig: {
        allowPortalCashWithdrawals:
          eisFirm?.whiteLabelConfig?.allowPortalCashWithdrawals ?? false,
        showMultipleOnInvestment:
          eisFirm?.whiteLabelConfig?.showMultipleOnInvestment ?? false,
        showMultipleOnInvestedCapital:
          eisFirm?.whiteLabelConfig?.showMultipleOnInvestedCapital ?? false,
        syndicatesArrangement:
          eisFirm?.whiteLabelConfig?.syndicatesArrangement ??
          SyndicatesArrangement.TaxYear,
        showInvestmentLink:
          eisFirm?.whiteLabelConfig?.showInvestmentLink ?? false,
        enableKyb: eisFirm?.whiteLabelConfig?.enableKyb ?? false,
        configurableTile1:
          eisFirm?.whiteLabelConfig?.configurableTile1 ?? TileType.MULTIPLE,
        configurableTile2:
          eisFirm?.whiteLabelConfig?.configurableTile2 ?? TileType.MULTIPLE,
        cashBalanceUsable:
          eisFirm?.whiteLabelConfig?.cashBalanceUsable ?? false,
        hideCashBalanceBefore:
          eisFirm?.whiteLabelConfig?.hideCashBalanceBefore ?? null,
        taxReliefDisplayMode:
          eisFirm?.whiteLabelConfig?.taxReliefDisplayMode ??
          TaxReliefDisplayMode.Forecast,
        companyBreakdownDisplayMode:
          eisFirm?.whiteLabelConfig?.companyBreakdownDisplayMode ??
          CompanyBreakdownDisplayMode.SeparateTab,
        investmentIdentifierDisplayMode:
          eisFirm?.whiteLabelConfig?.investmentIdentifierDisplayMode ??
          InvestmentIdentifierDisplayMode.ExternalInvestmentId,
        showFeeBreakdownToInvestors:
          eisFirm?.whiteLabelConfig?.showFeeBreakdownToInvestors ?? false,
        showAccruedFeesInFeeBreakdown:
          eisFirm?.whiteLabelConfig?.showAccruedFeesInFeeBreakdown ?? true,
        showSubscriptionPagePerformanceChart:
          eisFirm?.whiteLabelConfig?.showSubscriptionPagePerformanceChart ??
          ShowSubscriptionPagePerformanceChartMode.Never,
        syndicatePortalDisplayName:
          eisFirm?.whiteLabelConfig?.syndicatePortalDisplayName ?? '',
        manuallyRequestKybDocumentation:
          eisFirm?.whiteLabelConfig?.manuallyRequestKybDocumentation ?? false,
        proceedWithConsiderStatus:
          eisFirm?.whiteLabelConfig?.proceedWithConsiderStatus ?? false,
        checkInvestorAddress:
          eisFirm?.whiteLabelConfig?.checkInvestorAddress ?? false,
        kybServiceLink: eisFirm?.whiteLabelConfig?.kybServiceLink ?? '',
        customDocumentRequestText:
          eisFirm?.whiteLabelConfig?.customDocumentRequestText ?? '',
        permittedClassifications: eisFirm?.whiteLabelConfig
          ?.permittedClassifications ?? {
          everyday: true,
          sophisticated: true,
          highNetWorth: true,
          professional: true,
        },
        showAppropriatenessTest:
          eisFirm?.whiteLabelConfig?.showAppropriatenessTest ?? true,
        adviserIvcTemplate: eisFirm?.whiteLabelConfig?.adviserIvcTemplate
          ?.filePath
          ? {
              fileName:
                eisFirm.whiteLabelConfig.adviserIvcTemplate.fileName ?? '',
              filePath: eisFirm.whiteLabelConfig.adviserIvcTemplate.filePath,
            }
          : null,
        noSubscriptionsInvestorLandingPage:
          eisFirm?.whiteLabelConfig?.noSubscriptionsInvestorLandingPage ??
          NoSubscriptionsInvestorLandingPage.Portal,
      },
    },
    criteriaMode: 'all',
    reValidateMode: 'onSubmit',
  });

  const { handleSubmit, control, setValue, watch } = formMethods;

  const { isEnabled: dataRoomEnabled } = useFlag('data-room-enabled');

  const enableKyb = watch('whiteLabelConfig.enableKyb');
  const manuallyRequestKybDocumentation = watch(
    'whiteLabelConfig.manuallyRequestKybDocumentation',
  );
  const showFeeBreakdownToInvestors = watch(
    'whiteLabelConfig.showFeeBreakdownToInvestors',
  );

  const onSubmit = async (data) => {
    await updateEisFirm({ ...data, id });
  };

  return (
    <FormProvider {...formMethods}>
      <form>
        <Spacing>
          <PageSection title="Investor portal settings">
            <Spacing>
              <FieldRow
                title={'Show multiple on investment'}
                centerTitle
                tooltipText={
                  'In the tables in the investor/adviser portal, show or hide the "Multiple on investment" column.'
                }
              >
                <Controller
                  name="whiteLabelConfig.showMultipleOnInvestment"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      checked={!!value}
                      uncheckedLabel="No"
                      checkedLabel="Yes"
                    />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Show MOIC"
                centerTitle
                tooltipText={
                  'In the tables in the investor/adviser portal, show or hide the "Multiple on Invested Capital" column.'
                }
              >
                <Controller
                  name="whiteLabelConfig.showMultipleOnInvestedCapital"
                  disabled={!canEditFirm}
                  control={control}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      checked={!!value}
                      uncheckedLabel="No"
                      checkedLabel="Yes"
                      isControlled
                    />
                  )}
                />
              </FieldRow>

              <FieldRow title="Arrange syndicates" centerTitle>
                <Controller
                  name="whiteLabelConfig.syndicatesArrangement"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <GridContainer>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <AppSelectBox
                          data={[
                            {
                              value: 'taxYear',
                              label: 'By tax year',
                            },
                            {
                              value: 'calendarYear',
                              label: 'By calendar year',
                            },
                            {
                              value: 'all',
                              label: 'All together',
                            },
                          ]}
                          valueKey="value"
                          labelKey="label"
                          onChange={(e: { target: { value: any } }) =>
                            onChange(e.target.value)
                          }
                          error={!!error}
                          helperText={error?.message}
                          {...rest}
                        />
                      </Grid>
                    </GridContainer>
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Syndicate portal display name*"
                centerTitle
                tooltipText={
                  <span>
                    This text will show on your portal subscription table where
                    there are syndicate subscriptions. It will display as{' '}
                    <i>Firm name [your text] [year]</i> where a 'yearly' display
                    is selected or <i>Firm name [your text] subscriptions</i>{' '}
                    where 'all together' display is selected.
                  </span>
                }
              >
                <Controller
                  name="whiteLabelConfig.syndicatePortalDisplayName"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, ...rest },
                    fieldState: { error },
                  }) => (
                    <GridContainer>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <TextField
                          {...rest}
                          inputRef={ref}
                          fullWidth
                          error={!!error}
                          helperText={error?.message}
                        />
                      </Grid>
                    </GridContainer>
                  )}
                />
              </FieldRow>
              <FieldRow title="Show menu subscription link" centerTitle>
                <Controller
                  name="whiteLabelConfig.showInvestmentLink"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      checked={!!value}
                      uncheckedLabel="No"
                      checkedLabel="Yes"
                    />
                  )}
                />
              </FieldRow>
              {dataRoomEnabled && (
                <FieldRow
                  title="Default landing page for investors with no subscriptions"
                  centerTitle
                >
                  <Controller
                    name="whiteLabelConfig.noSubscriptionsInvestorLandingPage"
                    control={control}
                    disabled={!canEditFirm}
                    render={({ field: { ref, ...rest } }) => (
                      <GridContainer>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <AppSelectBox
                            {...rest}
                            ref={ref}
                            data={[
                              {
                                value:
                                  NoSubscriptionsInvestorLandingPage.Portal,
                                label: 'Portal always',
                              },
                              {
                                value:
                                  NoSubscriptionsInvestorLandingPage.InvestmentOpportunitiesOrPortal,
                                label:
                                  'Investment opportunities if there are live opportunities, portal if not',
                              },
                            ]}
                            valueKey="value"
                            labelKey="label"
                          />
                        </Grid>
                      </GridContainer>
                    )}
                  />
                </FieldRow>
              )}

              <FieldRow title="Allow portal cash withdrawals" centerTitle>
                <Controller
                  name="whiteLabelConfig.allowPortalCashWithdrawals"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      checked={!!value}
                      uncheckedLabel="No"
                      checkedLabel="Yes"
                      isControlled
                    />
                  )}
                />
              </FieldRow>

              <FieldRow
                title="Top centre portal tile"
                centerTitle
                tooltipText={
                  'This selection chooses what is displayed on the top centre tile of the investor "My portal" page.'
                }
              >
                <Controller
                  name="whiteLabelConfig.configurableTile1"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <ConfigurableTilesDropDown
                      onChange={onChange}
                      error={!!error}
                      errorMessage={error?.message}
                      {...rest}
                    />
                  )}
                />
              </FieldRow>

              <FieldRow
                title="Top right portal tile"
                centerTitle
                tooltipText={
                  'This selection chooses what is displayed on the top right tile of the investor "My portal" page.'
                }
              >
                <Controller
                  name="whiteLabelConfig.configurableTile2"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <ConfigurableTilesDropDown
                      onChange={onChange}
                      error={!!error}
                      errorMessage={error?.message}
                      {...rest}
                    />
                  )}
                />
              </FieldRow>

              <FieldRow
                title="Cash balance usable for new investments"
                centerTitle
              >
                <Controller
                  name="whiteLabelConfig.cashBalanceUsable"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      checked={!!value}
                      uncheckedLabel="No"
                      checkedLabel="Yes"
                      isControlled
                    />
                  )}
                />
              </FieldRow>

              <FieldRow title="Hide cash balance before" centerTitle>
                <Controller
                  name="whiteLabelConfig.hideCashBalanceBefore"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { onChange, value, ...rest },
                    fieldState: { error },
                  }) => (
                    <GridContainer>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <AppDatePicker
                          {...rest}
                          value={value?.toUTCDate() ?? null}
                          onChange={(date, context) => {
                            if (date && !context.validationError) {
                              onChange(new CalendarDay(date));
                            } else {
                              onChange(null);
                            }
                          }}
                          clearable
                          error={!!error}
                          helperText={error?.message}
                        />
                      </Grid>
                    </GridContainer>
                  )}
                />
              </FieldRow>
              <FieldRow title="Subscription identifier on table" centerTitle>
                <Controller
                  name="whiteLabelConfig.investmentIdentifierDisplayMode"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <GridContainer>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <AppSelectBox
                          data={[
                            {
                              value:
                                InvestmentIdentifierDisplayMode.ExternalInvestmentId,
                              label: 'External subscription ID',
                            },
                            {
                              value:
                                InvestmentIdentifierDisplayMode.SubscriptionDate,
                              label: 'Subscription date',
                            },
                          ]}
                          valueKey="value"
                          labelKey="label"
                          onChange={(e: { target: { value: any } }) =>
                            onChange(e.target.value)
                          }
                          error={!!error}
                          helperText={error?.message}
                          {...rest}
                        />
                      </Grid>
                    </GridContainer>
                  )}
                />
              </FieldRow>

              <FieldRow
                title="Tax relief displayed"
                centerTitle
                tooltipText={
                  <div>
                    This option changes the income tax relief figures shown in
                    tables on the portal. <i>Actual</i> displays income tax
                    relief for all holdings where S/EIS certificates have been
                    processed, accurate to subscription amounts. <i>Forecast</i>{' '}
                    displays an estimated tax relief, based on an individual's
                    initial subscription multiplied by the 'Income tax relief'
                    setting for the applicable tranche (adjustable in the
                    Tranches section)
                  </div>
                }
              >
                <Controller
                  name="whiteLabelConfig.taxReliefDisplayMode"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      onChange={(e) =>
                        setValue(
                          'whiteLabelConfig.taxReliefDisplayMode',
                          e.target.checked
                            ? TaxReliefDisplayMode.Actual
                            : TaxReliefDisplayMode.Forecast,
                        )
                      }
                      checked={value === TaxReliefDisplayMode.Actual}
                      uncheckedLabel="Forecast"
                      checkedLabel="Actual"
                      isControlled
                    />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Company-by-company breakdown display"
                centerTitle
              >
                <Controller
                  name="whiteLabelConfig.companyBreakdownDisplayMode"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      onChange={(e) =>
                        setValue(
                          'whiteLabelConfig.companyBreakdownDisplayMode',
                          e.target.checked
                            ? CompanyBreakdownDisplayMode.UnderSummaryTable
                            : CompanyBreakdownDisplayMode.SeparateTab,
                        )
                      }
                      checked={
                        value === CompanyBreakdownDisplayMode.UnderSummaryTable
                      }
                      uncheckedLabel="In separate tab"
                      checkedLabel="Under subscription summary table"
                      isControlled
                    />
                  )}
                />
              </FieldRow>

              <FieldRow
                title="Show fee breakdown to investors"
                centerTitle
                tooltipText="When enabled, investors will be able to see a breakdown of their fees in a separate tab on the subscription page."
              >
                <Controller
                  name="whiteLabelConfig.showFeeBreakdownToInvestors"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      checked={!!value}
                      uncheckedLabel="No"
                      checkedLabel="Yes"
                      isControlled
                    />
                  )}
                />
              </FieldRow>

              <FieldRow title="Show accrued fees in fee breakdown" centerTitle>
                <Controller
                  name="whiteLabelConfig.showAccruedFeesInFeeBreakdown"
                  control={control}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      checked={showFeeBreakdownToInvestors && !!value}
                      uncheckedLabel="No"
                      checkedLabel="Yes"
                      isControlled
                      disabled={!showFeeBreakdownToInvestors || !canEditFirm}
                    />
                  )}
                />
              </FieldRow>

              <FieldRow
                title="Show subscription page performance chart"
                centerTitle
                tooltipText={
                  'This setting determines when the fund performance bar chart is shown on each portal subscription page. The chart is hidden by default for syndicates that aren’t grouped by tax or calendar year.'
                }
              >
                <Controller
                  name="whiteLabelConfig.showSubscriptionPagePerformanceChart"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <GridContainer>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <AppSelectBox
                          data={[
                            {
                              value:
                                ShowSubscriptionPagePerformanceChartMode.Always,
                              label: 'Always',
                            },
                            {
                              value:
                                ShowSubscriptionPagePerformanceChartMode.AfterDeployment,
                              label: 'When deployment is complete',
                            },
                            {
                              value:
                                ShowSubscriptionPagePerformanceChartMode.Never,
                              label: 'Never',
                            },
                          ]}
                          valueKey="value"
                          labelKey="label"
                          onChange={(e: { target: { value: any } }) =>
                            onChange(e.target.value)
                          }
                          error={!!error}
                          helperText={error?.message}
                          {...rest}
                        />
                      </Grid>
                    </GridContainer>
                  )}
                />
              </FieldRow>
            </Spacing>
          </PageSection>

          <PageSection title="KYC/KYB settings" showDivider>
            <Spacing>
              <FieldRow
                title="KYC/KYB review recipient(s)"
                informationText="Separate emails by a semi-colon"
              >
                <Controller
                  name="kycKybReviewRecipient"
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, ...rest },
                    fieldState: { error },
                  }) => (
                    <>
                      <TextField
                        {...rest}
                        inputRef={ref}
                        placeholder="Enter the email addresses you wish to receive KYC/KYB review notifications. Team members with compliance oversight and sign-off ability should be entered here. Where no recipients are added, emails will be sent to the firm’s key contacts listed above. Key contacts will not be emailed if recipients are added here."
                        fullWidth
                        multiline
                        minRows={4}
                        error={!!error}
                        helperText={error?.message}
                      />
                    </>
                  )}
                />
              </FieldRow>

              <FieldRow
                title="Ongoing AML review recipient(s)"
                informationText="Separate emails by a semi-colon"
              >
                <Controller
                  name="amlReviewRecipient"
                  disabled={!canEditFirm}
                  render={({
                    field: { ref, ...rest },
                    fieldState: { error },
                  }) => (
                    <>
                      <TextField
                        {...rest}
                        inputRef={ref}
                        placeholder="Enter the email addresses you wish to receive ongoing AML review notifications. Team members with compliance oversight and sign-off ability should be entered here. Where no recipients are added, emails will be sent to the firm’s key contacts listed above. Key contacts will not be emailed if recipients are added here."
                        fullWidth
                        multiline
                        minRows={4}
                        error={!!error}
                        helperText={error?.message}
                      />
                    </>
                  )}
                />
              </FieldRow>

              <FieldRow
                title="Allow users to proceed with Consider status"
                centerTitle
                tooltipText={
                  <>
                    <b>Proceed with caution.</b> If KYC is completed and the
                    user does not pass, this setting allows the user to make a
                    subscription regardless.
                    <br />
                    <br />
                    Please note that custodians often reject applications
                    without completed KYC and, if you subsequently need users to
                    redo KYC, users will need to start the portal subscription
                    flow again to attempt it (even if not making a new
                    subscription).
                  </>
                }
              >
                <Controller
                  name="whiteLabelConfig.proceedWithConsiderStatus"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      checked={!!value}
                      uncheckedLabel="No"
                      checkedLabel="Yes"
                      isControlled
                    />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Automatically address check investors"
                centerTitle
                tooltipText="Upon an investor completing KYC, the system can check the investor's address against international databases to confirm if they are living there. This service does not work in all countries, but covers the UK, US and some European countries well. There is an additional £2 charge per check for this service."
              >
                <Controller
                  name="whiteLabelConfig.checkInvestorAddress"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      checked={!!value}
                      uncheckedLabel="Off"
                      checkedLabel="On"
                      isControlled
                    />
                  )}
                />
              </FieldRow>
              <FieldRow
                title="Legal entity KYB"
                centerTitle
                tooltipText={
                  <>
                    If KYB is switched on, the system will either (a) request
                    KYB documentation from investors or (b) direct investors
                    through the digital link or KYB service selected in the KYB
                    service link below.
                    <br />
                    <br />
                    If KYB is switched off, investors are told that you will
                    contact them to provide KYB information (which you can store
                    and approve on the relevant investor's View Investor page in
                    the KYB section).
                  </>
                }
              >
                <Controller
                  name="whiteLabelConfig.enableKyb"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      checked={!!value}
                      uncheckedLabel="Off"
                      checkedLabel="On"
                      isControlled
                      onChange={(e) => {
                        setValue(
                          'whiteLabelConfig.enableKyb',
                          e.target.checked,
                        );
                        if (e.target.checked) {
                          setValue('whiteLabelConfig.kybServiceLink', '');
                        } else {
                          setValue(
                            'whiteLabelConfig.manuallyRequestKybDocumentation',
                            false,
                          );
                          setValue(
                            'whiteLabelConfig.customDocumentRequestText',
                            '',
                          );
                        }
                      }}
                    />
                  )}
                />
              </FieldRow>
            </Spacing>
          </PageSection>

          {enableKyb && (
            <FieldRow
              title="Manually request KYB documentation"
              centerTitle
              tooltipText={
                <>
                  If you wish not to use a third party KYB service, you can
                  manually collect KYB documents for your review. This is useful
                  if you complement it with completing AML checks externally
                  (with a provider such as Veriphy).
                </>
              }
            >
              <Controller
                name="whiteLabelConfig.manuallyRequestKybDocumentation"
                control={control}
                disabled={!canEditFirm}
                render={({ field: { ref, value, ...rest } }) => (
                  <FieldSwitch
                    {...rest}
                    ref={ref}
                    checked={!!value}
                    uncheckedLabel="Off"
                    checkedLabel="On"
                    isControlled
                  />
                )}
              />
            </FieldRow>
          )}

          {manuallyRequestKybDocumentation && enableKyb && (
            <FieldRow
              title="Custom document request text"
              topTitle
              tooltipText={
                <>
                  Enter custom instructions for document upload. Text entered
                  here sits above a file upload tool, for investors to follow
                  when uploading files.
                  <br />
                  <br />
                  If you leave this field blank, the system automatically
                  requests constitutional documents and a registry extract
                  showing entity beneficial ownership. Anything you write here
                  overrides the standard system request list.
                </>
              }
            >
              <Controller
                name="whiteLabelConfig.customDocumentRequestText"
                control={control}
                render={({
                  field: { value, name },
                  formState: { isSubmitting },
                  fieldState: { error },
                }) => (
                  <>
                    <CkEditor
                      disabled={isSubmitting || !canEditFirm}
                      placeholder="Enter custom instructions for document upload."
                      onBlur={(value: string) => {
                        setValue(name, value);
                      }}
                      value={value}
                      extraLines={1}
                      showLists={false}
                    />
                    <p className={classes.errorText}>{error?.message}</p>
                  </>
                )}
              />
            </FieldRow>
          )}

          {enableKyb && !manuallyRequestKybDocumentation && (
            <FieldRow
              title="KYB service link"
              centerTitle
              tooltipText={
                <>
                  Enter the URL linking to your digital KYB provider of choice.
                  If you do not use an external KYB provider, please ensure{' '}
                  <i>Manually request KYB documentation</i> is turned on above.
                </>
              }
            >
              <Controller
                name="whiteLabelConfig.kybServiceLink"
                control={control}
                render={({ field: { ref, value }, fieldState: { error } }) => (
                  <GridContainer>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                      }}
                    >
                      <TextField
                        type="url"
                        placeholder="Enter KYB service URL"
                        inputRef={ref}
                        fullWidth
                        disabled={
                          manuallyRequestKybDocumentation ||
                          !enableKyb ||
                          !canEditFirm
                        }
                        error={!!error}
                        helperText={error?.message}
                        value={value || ''}
                        onChange={(e) => {
                          if (e.target.value === 'https://') {
                            setValue('whiteLabelConfig.kybServiceLink', '');
                          } else {
                            const newValue = e.target.value;
                            setValue(
                              'whiteLabelConfig.kybServiceLink',
                              newValue ? addHttps(newValue) : '',
                            );
                          }
                        }}
                      />
                    </Grid>
                  </GridContainer>
                )}
              />
            </FieldRow>
          )}

          <PageSection title="Portal sign-up flow settings" showDivider>
            <Spacing>
              <FieldRow
                title="Permitted classifications"
                centerTitle
                tooltipText="Select the UK FSMA and FCA COBS investor classifications users may select on your portal domain."
              >
                <Controller
                  name="whiteLabelConfig.permittedClassifications"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => {
                    const currentValue = value ?? {
                      everyday: false,
                      sophisticated: false,
                      highNetWorth: false,
                      professional: false,
                    };

                    return (
                      <CheckBoxRow>
                        <ClassificationCheckbox
                          label="Everyday"
                          fieldKey="everyday"
                          currentValue={currentValue}
                          onChange={onChange}
                          error={!!error}
                          errorMessage={error?.message}
                          disabled={!canEditFirm}
                        />
                        <ClassificationCheckbox
                          label="High net worth"
                          fieldKey="highNetWorth"
                          currentValue={currentValue}
                          onChange={onChange}
                          error={!!error}
                          errorMessage={error?.message}
                          disabled={!canEditFirm}
                        />
                        <ClassificationCheckbox
                          label="Sophisticated"
                          fieldKey="sophisticated"
                          currentValue={currentValue}
                          onChange={onChange}
                          error={!!error}
                          errorMessage={error?.message}
                          disabled={!canEditFirm}
                        />
                        <ClassificationCheckbox
                          label="Professional"
                          fieldKey="professional"
                          currentValue={currentValue}
                          onChange={onChange}
                          error={!!error}
                          errorMessage={error?.message}
                          tooltip={
                            <>
                              <b>Note:</b> legal entity users may only be
                              professional investors. If the professional
                              classification is unticked, legal entity users
                              will still be asked to classify as professional
                              investors whereas individual investors will not.
                            </>
                          }
                          disabled={!canEditFirm}
                        />
                      </CheckBoxRow>
                    );
                  }}
                />
              </FieldRow>

              <FieldRow
                title="Show appropriateness test"
                tooltipText="Show or hide the UK FCA COBS 4.12A appropriateness test. If you accept UK retail investors, toggling this off will make the investment flow non-compliant with COBS 4.12A."
              >
                <Controller
                  name="whiteLabelConfig.showAppropriatenessTest"
                  control={control}
                  disabled={!canEditFirm}
                  render={({ field: { ref, value, ...rest } }) => (
                    <FieldSwitch
                      {...rest}
                      ref={ref}
                      checked={!!value}
                      uncheckedLabel="No"
                      checkedLabel="Yes"
                      isControlled
                    />
                  )}
                />
              </FieldRow>

              <FieldRow
                title="Advised client IVC form"
                tooltipText="Upload your firm's IVC template for advisers to download, complete, and re-upload during the advised subscription flow."
              >
                <Controller
                  name="whiteLabelConfig.adviserIvcTemplate"
                  control={control}
                  disabled={!canEditFirm}
                  render={({
                    field: { value, name, onChange },
                    fieldState: { error: fieldError },
                  }) => (
                    <>
                      <Dropzone
                        name={name}
                        disabled={!canEditFirm}
                        accept={{
                          [FileType.PDF]: ['.pdf'],
                          [FileType.DOC]: ['.doc'],
                          [FileType.DOCX]: ['.docx'],
                        }}
                        maxFiles={1}
                        onDropFile={async (files) => {
                          if (!files?.[0]?.path) return;
                          const { data, error: uploadError } = await uploadFile(
                            files[0],
                            UploadType.AdviserIvcTemplate,
                          );
                          if (data) {
                            onChange({
                              fileName: files[0].name || '',
                              filePath: data.filePath,
                            });
                          } else {
                            error(uploadError.message);
                          }
                        }}
                        files={(value ? [value] : []).map((file) => ({
                          preview: file?.filePath ?? '',
                          fileName: file?.fileName ?? '',
                          path: file?.filePath ?? '',
                        }))}
                        onRemove={() => {
                          onChange(null);
                        }}
                      />
                      {fieldError && <p>{fieldError.message}</p>}
                    </>
                  )}
                />
              </FieldRow>
            </Spacing>
          </PageSection>

          <Grid size={{ xs: 4 }}>
            <ButtonGroup>
              <Link to={FIRM}>
                <Button variant="outlined">Cancel</Button>
              </Link>
              {canEditFirm ? (
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  loading={isUpdating}
                >
                  Save
                </Button>
              ) : null}
            </ButtonGroup>
          </Grid>
        </Spacing>
      </form>
    </FormProvider>
  );
};

export default InvestorPortalSettings;
